import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '../auth';

interface IndicesData {
  indices: string[];
  disabled_indices: string[];
}

export function IndicesManagement() {
  const { user } = useAuth();
  const [indicesData, setIndicesData] = useState<IndicesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [newIndexName, setNewIndexName] = useState('');
  const [addingIndex, setAddingIndex] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Проверяем, что пользователь - администратор
  if (!user || user.role !== 'Администратор') {
    return (
      <div className="container mx-auto max-w-7xl">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Управление индексами</h2>
              <p className="text-muted-foreground">Управление индексами Elasticsearch</p>
            </div>
            <Button variant="outline" asChild>
              <Link to="/admin">← Назад к обзору</Link>
            </Button>
          </div>
          
          <Alert className="border-red-500 bg-red-50">
            <AlertDescription className="text-red-700">
              Доступ запрещен. Эта страница доступна только администраторам.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const fetchIndices = async () => {
    try {
      const response = await fetch('/api/auth/admin/es_indices', {
        credentials: 'include',
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (response.ok) {
        const data = await response.json();
        console.log('Response data:', data);
        
        if (data.ok && data.data) {
          // Handle format from admin.py: {"ok": True, "data": {"indices": [...], "disabled_indices": [...]}}
          setIndicesData(data.data);
        } else if (data.indices || data.disabled_indices) {
          // Handle direct format: {"indices": [...], "disabled_indices": [...]}
          setIndicesData({
            indices: data.indices || [],
            disabled_indices: data.disabled_indices || []
          });
        } else {
          console.error('Unexpected response format:', data);
          setMessage({ type: 'error', text: 'Неожиданный формат ответа от сервера' });
        }
      } else {
        const responseText = await response.text();
        console.log('Error response text:', responseText);
        
        if (response.status === 403) {
          setMessage({ type: 'error', text: 'Доступ запрещен. Требуются права администратора.' });
        } else if (response.status === 401) {
          setMessage({ type: 'error', text: 'Токен аутентификации недействителен' });
        } else if (response.status === 404) {
          setMessage({ type: 'error', text: 'Endpoint не найден. Проверьте, что backend запущен.' });
        } else {
          setMessage({ type: 'error', text: `Ошибка HTTP: ${response.status}. Ответ: ${responseText.substring(0, 100)}` });
        }
      }
    } catch (error) {
      console.error('Error fetching indices:', error);
      setMessage({ type: 'error', text: 'Ошибка сети при загрузке индексов' });
    } finally {
      setLoading(false);
    }
  };

  const addIndex = async () => {
    if (!newIndexName.trim()) return;

    setAddingIndex(true);
    try {
      const formData = new FormData();
      formData.append('index_name', newIndexName.trim());

             const response = await fetch('/api/auth/admin/es_indices', {
         method: 'POST',
         credentials: 'include',
         body: formData
       });

      const data = await response.json();
      
      if (response.ok && (data.ok || data.message)) {
        setMessage({ type: 'success', text: data.message || 'Индекс добавлен' });
        setNewIndexName('');
        fetchIndices();
      } else if (response.status === 403) {
        setMessage({ type: 'error', text: 'Доступ запрещен. Требуются права администратора.' });
      } else if (response.status === 401) {
        setMessage({ type: 'error', text: 'Токен аутентификации недействителен' });
      } else {
        setMessage({ type: 'error', text: data.error || data.detail || `Ошибка HTTP: ${response.status}` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка сети' });
    } finally {
      setAddingIndex(false);
    }
  };

  const disableIndex = async (indexName: string) => {
    try {
             const response = await fetch(`/api/auth/admin/es_indices/${encodeURIComponent(indexName)}/disable`, {
         method: 'POST',
         credentials: 'include',
       });

      const data = await response.json();
      
      if (response.ok && (data.ok || data.message)) {
        setMessage({ type: 'success', text: data.message || 'Индекс отключен' });
        fetchIndices();
      } else {
        setMessage({ type: 'error', text: data.error || data.detail || 'Ошибка отключения индекса' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка сети' });
    }
  };

  const enableIndex = async (indexName: string) => {
    try {
             const response = await fetch(`/api/auth/admin/es_indices/${encodeURIComponent(indexName)}/enable`, {
         method: 'POST',
         credentials: 'include',
       });

      const data = await response.json();
      
      if (response.ok && (data.ok || data.message)) {
        setMessage({ type: 'success', text: data.message || 'Индекс включен' });
        fetchIndices();
      } else {
        setMessage({ type: 'error', text: data.error || data.detail || 'Ошибка включения индекса' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка сети' });
    }
  };

  const deleteIndex = async (indexName: string) => {
    if (!confirm(`Вы уверены, что хотите полностью удалить индекс "${indexName}"?`)) {
      return;
    }

    try {
             const response = await fetch(`/api/auth/admin/es_indices/${encodeURIComponent(indexName)}`, {
         method: 'DELETE',
         credentials: 'include',
       });

      const data = await response.json();
      
      if (response.ok && (data.ok || data.message)) {
        setMessage({ type: 'success', text: data.message || 'Индекс удален' });
        fetchIndices();
      } else {
        setMessage({ type: 'error', text: data.error || data.detail || 'Ошибка удаления индекса' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Ошибка сети' });
    }
  };

  useEffect(() => {
    if (user && user.role === 'Администратор') {
      fetchIndices();
    }
  }, [user]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  if (loading) {
    return (
      <div className="container mx-auto max-w-7xl">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Управление индексами</h2>
              <p className="text-muted-foreground">Управление индексами Elasticsearch</p>
            </div>
            <Button variant="outline" asChild>
              <Link to="/admin">← Назад к обзору</Link>
            </Button>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Загрузка...</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p>Загрузка индексов...</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-7xl">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Управление индексами</h2>
            <p className="text-muted-foreground">Управление индексами Elasticsearch для поиска</p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/admin">← Назад к обзору</Link>
          </Button>
        </div>

        {message && (
          <Alert className={message.type === 'error' ? 'border-red-500 bg-red-50' : 'border-green-500 bg-green-50'}>
            <AlertDescription className={message.type === 'error' ? 'text-red-700' : 'text-green-700'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        {/* Добавление нового индекса */}
        <Card>
          <CardHeader>
            <CardTitle>Добавить новый индекс</CardTitle>
            <CardDescription>
              Добавление нового индекса для поиска в Elasticsearch
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex space-x-4">
              <div className="flex-1">
                <Label htmlFor="indexName">Название индекса</Label>
                <Input
                  id="indexName"
                  value={newIndexName}
                  onChange={(e) => setNewIndexName(e.target.value)}
                  placeholder="Введите название индекса"
                  disabled={addingIndex}
                />
              </div>
              <div className="flex items-end">
                <Button 
                  onClick={addIndex} 
                  disabled={addingIndex || !newIndexName.trim()}
                >
                  {addingIndex ? 'Добавление...' : 'Добавить'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Активные и отключенные индексы в 2 колонки */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Активные индексы */}
          <Card>
            <CardHeader>
              <CardTitle>Активные индексы ({indicesData?.indices?.length || 0})</CardTitle>
              <CardDescription>
                Индексы, которые используются для поиска
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!indicesData?.indices?.length ? (
                <p className="text-muted-foreground">Нет активных индексов</p>
              ) : (
                <div className="space-y-3">
                  {indicesData.indices.map((index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg bg-green-50 dark:bg-green-900/20">
                      <div className="flex items-center space-x-3">
                        <Badge variant="default" className="bg-green-600">Активен</Badge>
                        <span className="font-medium">{index}</span>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => disableIndex(index)}
                        >
                          Отключить
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => deleteIndex(index)}
                        >
                          Удалить
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Отключенные индексы */}
          <Card>
            <CardHeader>
              <CardTitle>Отключенные индексы ({indicesData?.disabled_indices?.length || 0})</CardTitle>
              <CardDescription>
                Индексы, которые временно отключены от поиска
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!indicesData?.disabled_indices?.length ? (
                <p className="text-muted-foreground">Нет отключенных индексов</p>
              ) : (
                <div className="space-y-3">
                  {indicesData.disabled_indices.map((index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg bg-yellow-50 dark:bg-yellow-900/20">
                      <div className="flex items-center space-x-3">
                        <Badge variant="secondary" className="bg-yellow-600">Отключен</Badge>
                        <span className="font-medium text-muted-foreground">{index}</span>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => enableIndex(index)}
                        >
                          Включить
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => deleteIndex(index)}
                        >
                          Удалить
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
